import { Fragment, useEffect, useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryFilterSelect,
  RegistryPageBtn,
  RegistryPageButtons,
  RegistryPagination,
  RegistrySearchBox,
  RegistryToolbar,
  paginationWindow,
} from '@shared/ui/RegistryShell';
import { useCreateNpaEventMutation, useGetNpaDigestQuery, useListNpaQuery } from '@shared/api';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { useAgentPermission } from '@entities/user';

const PAGE_SIZE = 25;

const SOURCE_OPTIONS = [
  { value: '', label: 'Все источники' },
  { value: 'consultant', label: 'КонсультантПлюс' },
  { value: 'garant', label: 'Гарант' },
  { value: 'pravo_gov', label: 'pravo.gov.ru' },
  { value: 'kad_arbitr', label: 'kad.arbitr.ru' },
  { value: 'manual', label: 'Вручную' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Любой тип' },
  { value: 'law', label: 'НПА' },
  { value: 'court_practice', label: 'Судебная практика' },
  { value: 'review', label: 'Обзор' },
  { value: 'alert', label: 'Важное' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'Любая' },
  { value: 'critical', label: 'Критично' },
  { value: 'warning', label: 'Существенно' },
  { value: 'info', label: 'Информ.' },
];

const WINDOW_OPTIONS = [
  { value: 7, label: 'Последние 7 дней' },
  { value: 14, label: 'Последние 14 дней' },
  { value: 30, label: 'Последний месяц' },
  { value: 90, label: 'Последний квартал' },
  { value: 365, label: 'Последний год' },
];

const sourceLabel: Record<string, string> = Object.fromEntries(
  SOURCE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);
const typeLabel: Record<string, string> = Object.fromEntries(
  TYPE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

function severityTone(s: string): 'danger' | 'warning' | 'success' | 'neutral' {
  if (s === 'critical') return 'danger';
  if (s === 'warning') return 'warning';
  if (s === 'info') return 'success';
  return 'neutral';
}
function severityLabel(s: string): string {
  return { critical: 'Критично', warning: 'Существенно', info: 'Информ.' }[s] ?? s;
}

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DigestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const DigestCell = styled.div<{ $tone?: 'danger' | 'warning' | 'success' }>`
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ $tone, theme }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'warning'
          ? theme.colors.status.warning
          : $tone === 'success'
            ? theme.colors.status.success
            : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};

  & > .num {
    font-size: 22px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
    line-height: 1;
  }
  & > .lab {
    margin-top: 4px;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.text.muted};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

const TagPill = styled.span`
  display: inline-block;
  padding: 1px 8px;
  margin: 0 4px 4px 0;
  font-size: 11px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 999px;
`;

function fmt(s: string): string {
  return new Date(s).toLocaleString('ru-RU');
}

export function Monitoring() {
  const { can_write } = useAgentPermission('statut');
  const [windowDays, setWindowDays] = useState(30);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [source, setSource] = useState('');
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [windowDays, search, source, eventType, severity]);

  const queryArg = useMemo(
    () => ({
      search: search || undefined,
      source: source || undefined,
      event_type: eventType || undefined,
      severity: severity || undefined,
      since_days: windowDays,
      sort: 'occurred_at',
      order: 'desc' as const,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [search, source, eventType, severity, windowDays, page],
  );

  const { data, isLoading, isFetching } = useListNpaQuery(queryArg);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pages = paginationWindow(page, totalPages);

  const { data: digest } = useGetNpaDigestQuery({ days: windowDays });

  const [createEvent, { isLoading: isCreating }] = useCreateNpaEventMutation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [url, setUrl] = useState('');
  const [newSource, setNewSource] = useState('manual');
  const [newType, setNewType] = useState('law');
  const [newSeverity, setNewSeverity] = useState('info');
  const [tags, setTags] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await createEvent({
      source: newSource,
      event_type: newType,
      title,
      summary: summary || undefined,
      url: url || undefined,
      severity: newSeverity,
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    }).unwrap();
    setTitle('');
    setSummary('');
    setUrl('');
    setTags('');
    setOpen(false);
  }

  return (
    <>
      <Note>
        Мониторинг изменений законодательства и судебной практики. Cron раз в сутки собирает новые
        НПА и решения из подключённых источников (КонсультантПлюс, Гарант, pravo.gov.ru,
        kad.arbitr.ru). До подключения реальных API можно добавлять важные события вручную.
      </Note>

      {digest && (
        <DigestGrid>
          <DigestCell>
            <div className="num">{digest.counts.total}</div>
            <div className="lab">Всего за {digest.days} дн.</div>
          </DigestCell>
          <DigestCell $tone={digest.counts.critical > 0 ? 'danger' : undefined}>
            <div className="num">{digest.counts.critical}</div>
            <div className="lab">Критично</div>
          </DigestCell>
          <DigestCell $tone={digest.counts.warning > 0 ? 'warning' : undefined}>
            <div className="num">{digest.counts.warning}</div>
            <div className="lab">Существенно</div>
          </DigestCell>
          <DigestCell>
            <div className="num">{digest.counts.laws}</div>
            <div className="lab">НПА</div>
          </DigestCell>
          <DigestCell>
            <div className="num">{digest.counts.court_practice}</div>
            <div className="lab">Судебная практика</div>
          </DigestCell>
          <DigestCell>
            <div className="num">{digest.counts.reviews}</div>
            <div className="lab">Обзоры</div>
          </DigestCell>
        </DigestGrid>
      )}

      <RegistryToolbar>
        <RegistrySearchBox
          placeholder="Поиск по заголовку или аннотации…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <RegistryFilterSelect
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
        >
          {WINDOW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect value={source} onChange={(e) => setSource(e.target.value)}>
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect value={eventType} onChange={(e) => setEventType(e.target.value)}>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <Button
          onClick={() => setOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Событие
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <RegistryFilterSelect
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
          style={{ marginLeft: 'auto' }}
        >
          {SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Дата</th>
              <th>Источник</th>
              <th>Тип</th>
              <th>Серьёзность</th>
              <th>Заголовок</th>
              <th>Теги</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width={100} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={120} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={100} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={150} $height={14} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={6}>
                  {search || source || eventType || severity
                    ? 'По заданным фильтрам ничего не найдено.'
                    : 'Событий не зафиксировано. Подключите источники или добавьте важное вручную.'}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(e.occurred_at)}</td>
                  <td>
                    <Badge>{sourceLabel[e.source] ?? e.source}</Badge>
                  </td>
                  <td>
                    <Badge>{typeLabel[e.event_type] ?? e.event_type}</Badge>
                  </td>
                  <td>
                    <Badge $tone={severityTone(e.severity)}>{severityLabel(e.severity)}</Badge>
                  </td>
                  <td>
                    {e.url ? (
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--osnova-primary, #5a8dee)' }}
                      >
                        {e.title}
                      </a>
                    ) : (
                      e.title
                    )}
                    {e.summary && (
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--osnova-text-muted, #888)',
                          marginTop: 2,
                          maxWidth: 480,
                        }}
                      >
                        {e.summary}
                      </div>
                    )}
                  </td>
                  <td>
                    {e.tags.length === 0 ? '—' : e.tags.map((t) => <TagPill key={t}>{t}</TagPill>)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {total > PAGE_SIZE && (
        <RegistryPagination>
          <span>
            Стр. {page + 1} из {totalPages} · показано {items.length} из {total}
          </span>
          <RegistryPageButtons>
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Назад
            </RegistryPageBtn>
            {pages.map((p, idx) => {
              const prev = idx > 0 ? pages[idx - 1] : -1;
              const gap = idx > 0 && p - prev > 1;
              return (
                <Fragment key={p}>
                  {gap && <RegistryPageBtn disabled>…</RegistryPageBtn>}
                  <RegistryPageBtn type="button" $active={p === page} onClick={() => setPage(p)}>
                    {p + 1}
                  </RegistryPageBtn>
                </Fragment>
              );
            })}
            <RegistryPageBtn
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Вперёд →
            </RegistryPageBtn>
          </RegistryPageButtons>
        </RegistryPagination>
      )}

      <Modal open={open} title="Добавить событие мониторинга" onClose={() => setOpen(false)}>
        <form onSubmit={onSubmit}>
          <Field>
            <Label htmlFor="npa-title">Заголовок *</Label>
            <Input
              id="npa-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="напр., Постановление Пленума ВС от 12.05.2026 № 14"
              required
            />
          </Field>
          <Field>
            <Label htmlFor="npa-summary">Аннотация</Label>
            <textarea
              id="npa-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              style={{
                width: '100%',
                minHeight: 80,
                padding: 8,
                borderRadius: 6,
                border: '1px solid var(--osnova-border, #2a2a2a)',
                background: 'var(--osnova-bg-elevated, #1a1a1a)',
                color: 'inherit',
                font: 'inherit',
                fontSize: 13,
                resize: 'vertical',
              }}
            />
          </Field>
          <Field>
            <Label htmlFor="npa-url">URL источника</Label>
            <Input
              id="npa-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
            />
          </Field>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
            }}
          >
            <Field>
              <Label htmlFor="npa-src">Источник</Label>
              <Select id="npa-src" value={newSource} onChange={(e) => setNewSource(e.target.value)}>
                {SOURCE_OPTIONS.filter((o) => o.value).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="npa-type">Тип</Label>
              <Select id="npa-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
                {TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="npa-sev">Серьёзность</Label>
              <Select
                id="npa-sev"
                value={newSeverity}
                onChange={(e) => setNewSeverity(e.target.value)}
              >
                {SEVERITY_OPTIONS.filter((o) => o.value).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field>
            <Label htmlFor="npa-tags">Теги (через запятую)</Label>
            <Input
              id="npa-tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="ст. 395 ГК, 44-ФЗ, строительство"
            />
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="submit"
              disabled={isCreating || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating ? 'Сохраняем…' : 'Добавить'}
            </Button>
            <Button type="button" $variant="secondary" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
